import { REACT_APP_CONFIG_URL } from "src/config/environment"
import {
    ConfigAuthorizationFlow as GeneratedConfigAuthorizationFlow,
    CrearCodigoAutorizacionConfigInput,
} from "src/gql/schema"

export const CONFIG_CLIENT_ID = "easyroom-config"
export const CONFIG_AUTHORIZATION_FLOWS = ["CONFIGURACION_HOTEL", "ALTA_HOTEL"] as const

export type ConfigAuthorizationFlow = (typeof CONFIG_AUTHORIZATION_FLOWS)[number]

export interface ConfigAuthorizationRequest {
    code_challenge: string
    flujo: ConfigAuthorizationFlow
    hotel_id: string
    state: string
}

export type ConfigAuthorizationValidation =
    | { valid: true; request: ConfigAuthorizationRequest }
    | { valid: false; error: string }

const LOCAL_CONFIG_URL = "http://localhost:5173"
const REQUIRED_AUTHORIZATION_PARAMS = [
    "response_type",
    "client_id",
    "redirect_uri",
    "state",
    "code_challenge",
    "code_challenge_method",
    "flujo",
    "hotel_id",
    "path"
] as const
const STATE_PATTERN = /^[A-Za-z0-9._~-]{16,256}$/
const CODE_CHALLENGE_PATTERN = /^[A-Za-z0-9_-]{43}$/
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export const getConfigOrigin = (
    configuredUrl = REACT_APP_CONFIG_URL,
    environment = process.env.NODE_ENV
): string | null => {
    const rawUrl = configuredUrl?.trim() || (environment === "production" ? "" : LOCAL_CONFIG_URL)

    if (!rawUrl) {
        console.log("REACT_APP_CONFIG_URL no está configurada para este ambiente.")
        return null
    }

    const url = (() => {
        try {
            return new URL(rawUrl)
        } catch {
            return null
        }
    })()

    if (!url) {
        console.log("REACT_APP_CONFIG_URL no contiene una URL válida.")
        return null
    }

    if (!["http:", "https:"].includes(url.protocol) || url.username || url.password) {
        console.log("REACT_APP_CONFIG_URL debe ser un origen HTTP(S) válido.")
        return null
    }

    if ((url.pathname && url.pathname !== "/") || url.search || url.hash) {
        console.log("REACT_APP_CONFIG_URL debe contener únicamente el origen de configuración.")
        return null
    }

    return url.origin
}

export const getConfigCallbackUrl = (
    configuredUrl = REACT_APP_CONFIG_URL,
    environment = process.env.NODE_ENV
): string | null => {
    const origin = getConfigOrigin(configuredUrl, environment)
    return origin ? `${origin}/auth/callback` : null
}

export const buildConfigAuthorizationUrl = (
    flujo: ConfigAuthorizationFlow,
    hotel_id: string,
    configuredUrl = REACT_APP_CONFIG_URL,
    environment = process.env.NODE_ENV,
    path?: string
): string | null => {
    if (!CONFIG_AUTHORIZATION_FLOWS.includes(flujo)) {
        console.log("El flujo de configuración no es válido.")
        return null
    }
    if (!UUID_PATTERN.test(hotel_id)) {
        console.log("No se encontró un hotel válido para iniciar la configuración.")
        return null
    }

    const origin = getConfigOrigin(configuredUrl, environment)
    if (!origin) return null

    const url = new URL("/auth/start", origin)
    url.searchParams.set("flujo", flujo)
    url.searchParams.set("hotel_id", hotel_id)

    if (path) {
        url.searchParams.set("path", path)
    }
    
    return url.toString()
}

export const parseConfigAuthorizationRequest = (
    search: string,
    configuredUrl = REACT_APP_CONFIG_URL,
    environment = process.env.NODE_ENV
): ConfigAuthorizationValidation => {
    const params = new URLSearchParams(search)
    const unexpectedParams: string[] = []

    params.forEach((_value, key) => {
        if (!REQUIRED_AUTHORIZATION_PARAMS.includes(key as (typeof REQUIRED_AUTHORIZATION_PARAMS)[number])) {
            unexpectedParams.push(key)
        }
    })

    if (unexpectedParams.length) {
        return { valid: false, error: `Parámetro de autorización no permitido: ${unexpectedParams[0]}.` }
    }

    for (const name of REQUIRED_AUTHORIZATION_PARAMS) {
        if (params.getAll(name).length !== 1) {
            return { valid: false, error: `El parámetro ${name} es obligatorio y debe aparecer una sola vez.` }
        }
    }

    const trustedRedirectUri = getConfigCallbackUrl(configuredUrl, environment)
    if (!trustedRedirectUri) {
        return { valid: false, error: "La URL de configuración no es válida." }
    }

    if (params.get("response_type") !== "code") {
        return { valid: false, error: "response_type debe ser code." }
    }
    if (params.get("client_id") !== CONFIG_CLIENT_ID) {
        return { valid: false, error: "El cliente de configuración no es válido." }
    }
    if (params.get("redirect_uri") !== trustedRedirectUri) {
        return { valid: false, error: "La URL de retorno no coincide con el cliente de configuración." }
    }
    if (params.get("code_challenge_method") !== "S256") {
        return { valid: false, error: "code_challenge_method debe ser S256." }
    }

    const state = params.get("state") || ""
    if (!STATE_PATTERN.test(state)) {
        return { valid: false, error: "state debe tener entre 16 y 256 caracteres seguros para URL." }
    }

    const code_challenge = params.get("code_challenge") || ""
    if (!CODE_CHALLENGE_PATTERN.test(code_challenge)) {
        return { valid: false, error: "code_challenge debe ser base64url de 43 caracteres." }
    }

    const flujo = params.get("flujo") || ""
    if (!CONFIG_AUTHORIZATION_FLOWS.includes(flujo as ConfigAuthorizationFlow)) {
        return { valid: false, error: "El flujo de configuración no es válido." }
    }

    const hotel_id = params.get("hotel_id") || ""
    if (!UUID_PATTERN.test(hotel_id)) {
        return { valid: false, error: "hotel_id debe ser un UUID válido." }
    }

    return {
        valid: true,
        request: {
            code_challenge,
            flujo: flujo as ConfigAuthorizationFlow,
            hotel_id,
            state,
        },
    }
}

export const buildAuthorizationCodeInput = (
    request: ConfigAuthorizationRequest,
    configuredUrl = REACT_APP_CONFIG_URL,
    environment = process.env.NODE_ENV
): CrearCodigoAutorizacionConfigInput | null => {
    const redirect_uri = getConfigCallbackUrl(configuredUrl, environment)
    if (!redirect_uri) return null

    return {
        client_id: CONFIG_CLIENT_ID,
        redirect_uri,
        code_challenge: request.code_challenge,
        code_challenge_method: "S256",
        flujo:
            request.flujo === "ALTA_HOTEL"
                ? GeneratedConfigAuthorizationFlow.AltaHotel
                : GeneratedConfigAuthorizationFlow.ConfiguracionHotel,
        hotel_id: request.hotel_id,
    }
}

export const buildConfigCallbackResponseUrl = (
    code: string,
    state: string,
    configuredUrl = REACT_APP_CONFIG_URL,
    environment = process.env.NODE_ENV
): string | null => {
    const callbackUrl = getConfigCallbackUrl(configuredUrl, environment)
    if (!callbackUrl) return null

    const callback = new URL(callbackUrl)
    const params = new URLSearchParams(location.search)
    
    const path = params.get("path") || "home"

    callback.searchParams.set("code", code)
    callback.searchParams.set("state", state)
    callback.searchParams.set("path", path)
    return callback.toString()
}
